import { useCallback, useEffect, useState } from 'react';
import useSystemLoading from 'utils/hooks/useSystemLoading';
import { useLazyQuery, useMutation } from '@apollo/client';
import { JobFamilyGroupsModel, JobFamilyGroupQueryModelInput, PageModelInput } from '@utmost.co/enterprise-models';
import { DataTableTypes } from '@utmost.co/utmost-design-system';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';
import { GET_JOB_FAMILY_GROUPS, REMOVE_JOB_FAMILY_GROUP } from './jobFamilyGroups.graphql';
import { useJobFamilyGroupsIntl } from './translations/useJobFamilyGroupsIntl';

export type JobFamilyQuery = DataTableTypes.OnVariablesChanged<{ search: string }, 'name'>;

export function useJobFamilies() {
  const intl = useJobFamilyGroupsIntl();
  const { showSuccess, showError } = useSnackbar();

  const [variables, setVariables] = useState<JobFamilyQuery | undefined>({
    page: { offset: 0, limit: 40 },
    query: { search: '' }
  });

  const [getFamilies, { data: jobFamilyGroups, loading, refetch }] = useLazyQuery<
    {
      jobFamilyGroups: JobFamilyGroupsModel;
    },
    DeepPartial<{ query: JobFamilyGroupQueryModelInput; page: PageModelInput }>
  >(GET_JOB_FAMILY_GROUPS);

  useEffect(() => {
    getFamilies({ variables: { query: variables?.query, page: variables?.page } });
  }, [getFamilies, variables?.page, variables?.query]);

  useSystemLoading(loading);

  const [removeJobFamilyGroup, { loading: removeLoading }] = useMutation<
    { deleteJobFamilyGroup: { uid: string } },
    { uid: string }
  >(REMOVE_JOB_FAMILY_GROUP, {
    onError: (e) => showError(e.message)
  });

  const remove = useCallback(
    async (uids: string[]) => {
      await Promise.all(uids.map((uid) => removeJobFamilyGroup({ variables: { uid } })));
      showSuccess(intl.jobFamilyGroupDeletedSuccessfully);
    },
    [intl.jobFamilyGroupDeletedSuccessfully, removeJobFamilyGroup, showSuccess]
  );

  useSystemLoading(removeLoading || loading);

  return {
    getFamilies,
    loading,
    remove,
    totalCount: jobFamilyGroups?.jobFamilyGroups?.totalCount || 0,
    jobFamilies: jobFamilyGroups?.jobFamilyGroups?.items || [],
    variables,
    refetch,
    setVariables
  };
}
